import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Layout.css';
import { Sun, Moon, Monitor, ChevronDown, User, LogOut, Settings, Map, X, ChevronLeft, ChevronRight } from 'lucide-react';
import ResourceGroupSelector from '../ResourceGroupSelector/ResourceGroupSelector';
import { useTranslation } from 'react-i18next';
import organizationService from '../../services/organizationService';
import { getApiOrigin } from '../../services/config';

const Layout = ({ children, handleLogout, currentOrganization, currentResourceGroup, onResourceGroupChange, resourceGroups, isResourceGroupsLoading, isAuthenticated, currentUser }) => {
    const location = useLocation();
    const isRoomPage = location.pathname.startsWith('/room/');

    const [theme, setTheme] = React.useState(() => {
        return localStorage.getItem('theme') || 'system';
    });

    const [language, setLanguage] = React.useState(() => {
        return localStorage.getItem('i18nextLng') || 'en';
    });

    const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
    const dropdownRef = React.useRef(null);
    const [hasManagementAccess, setHasManagementAccess] = React.useState(false);
    const [isFloorPlanOpen, setIsFloorPlanOpen] = React.useState(false);
    const [floorPlanIndex, setFloorPlanIndex] = React.useState(0);

    const [systemPrefersDark, setSystemPrefersDark] = React.useState(() =>
        window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
    );

    const { t, i18n } = useTranslation();

    const floorPlanSrc = React.useMemo(() => {
        const urls = currentResourceGroup?.floor_plan_images;
        if (Array.isArray(urls) && urls.length > 0) {
            return urls;
        }
        const single = currentResourceGroup?.floor_plan_image;
        if (!single) return [];
        if (/^https?:\/\//i.test(single)) return [single];
        const origin = getApiOrigin();
        if (!origin) return [single];
        if (single.startsWith('/')) return [`${origin}${single}`];
        return [`${origin}/${single}`];
    }, [currentResourceGroup]);

    React.useEffect(() => {
        setIsFloorPlanOpen(false);
        setFloorPlanIndex(0);
    }, [currentResourceGroup?.id]);

    React.useEffect(() => {
        if (!isFloorPlanOpen) return;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') setIsFloorPlanOpen(false);
            if (e.key === 'ArrowLeft') setFloorPlanIndex((i) => Math.max(0, i - 1));
            if (e.key === 'ArrowRight') setFloorPlanIndex((i) => Math.min(floorPlanSrc.length - 1, i + 1));
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isFloorPlanOpen, floorPlanSrc.length]);

    React.useEffect(() => {
        const root = document.documentElement;
        if (theme === 'system') {
            root.removeAttribute('data-theme');
            localStorage.removeItem('theme');
        } else {
            root.setAttribute('data-theme', theme);
            localStorage.setItem('theme', theme);
        }
    }, [theme]);

    React.useEffect(() => {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = (e) => setSystemPrefersDark(e.matches);
        mq.addEventListener?.('change', onChange);
        return () => mq.removeEventListener?.('change', onChange);
    }, []);

    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    React.useEffect(() => {
        const checkManagementAccess = async () => {
            if (!isAuthenticated || !currentUser) {
                setHasManagementAccess(false);
                return;
            }

            const orgRole = currentOrganization?.user_role;
            const canManageOrg = orgRole === 'OWNER' || orgRole === 'ADMIN';
            let canManageGroup = false;

            try {
                const groups = await organizationService.getResourceGroups(null);
                canManageGroup = groups.some((group) => group.user_role === 'MANAGER');
            } catch (error) {
                canManageGroup = false;
            }

            setHasManagementAccess(Boolean(currentUser.is_staff) || canManageOrg || canManageGroup);
        };

        checkManagementAccess();
    }, [isAuthenticated, currentUser, currentOrganization]);

    const handleLanguageChange = (newLanguage) => {
        setLanguage(newLanguage);
        i18n.changeLanguage(newLanguage);
        localStorage.setItem('i18nextLng', newLanguage);
    };

    const handleThemeChange = (newTheme) => {
        setTheme(newTheme);
    };

    const getThemeIcon = () => {
        const isDark = theme === 'dark' || (theme === 'system' && systemPrefersDark);
        const isSystem = theme === 'system';
        if (isSystem) return <Monitor size={18} aria-hidden="true" />;
        return isDark ? <Moon size={18} aria-hidden="true" /> : <Sun size={18} aria-hidden="true" />;
    };
    return (
        <div className="layout-root">
            {/* Navigation bar - hidden on room page */}
            {!isRoomPage && (
                <nav className="app-nav">
                    <div className="app-links">
                        <Link to="/" className="app-link">
                            {t('nav_home')}
                        </Link>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {isAuthenticated && currentOrganization && (
                            <ResourceGroupSelector
                                onChange={onResourceGroupChange}
                                currentOrganization={currentOrganization}
                                resourceGroups={resourceGroups}
                                currentResourceGroup={currentResourceGroup}
                                isLoading={isResourceGroupsLoading}
                            />
                        )}

                        {isAuthenticated && floorPlanSrc.length > 0 && (
                            <button
                                type="button"
                                className="floorplan-trigger"
                                onClick={() => {
                                    setFloorPlanIndex(0);
                                    setIsFloorPlanOpen(true);
                                }}
                            >
                                <Map size={18} aria-hidden="true" />
                                <span>{t('floor_plan')}</span>
                            </button>
                        )}

                        {isAuthenticated && currentUser && (
                            <div className="user-dropdown-container" ref={dropdownRef}>
                                <button
                                    className="user-dropdown-trigger"
                                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                    aria-expanded={isDropdownOpen}
                                    aria-haspopup="true"
                                >
                                    <User size={18} />
                                    <span className="username">
                                        {currentUser.first_name || currentUser.last_name
                                            ? `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim()
                                            : currentUser.username}
                                    </span>
                                    <ChevronDown size={16} className={`chevron ${isDropdownOpen ? 'open' : ''}`} />
                                </button>

                                {isDropdownOpen && (
                                    <div className="user-dropdown-menu">
                                        <div className="dropdown-section">
                                            <div className="dropdown-item-text">
                                                <small style={{ color: 'var(--text-secondary)' }}>{t('signed_in_as')}</small>
                                                <strong>{currentUser.username}</strong>
                                            </div>
                                        </div>

                                        <div className="dropdown-divider"></div>

                                        <div className="dropdown-section">
                                            <div className="dropdown-item">
                                                <div className="dropdown-item-header">
                                                    {getThemeIcon()}
                                                    <span>{t('theme')}</span>
                                                </div>
                                                <select
                                                    className="dropdown-select"
                                                    value={theme}
                                                    onChange={(e) => handleThemeChange(e.target.value)}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <option value="system">{t('theme_system')}</option>
                                                    <option value="light">{t('theme_light')}</option>
                                                    <option value="dark">{t('theme_dark')}</option>
                                                </select>
                                            </div>

                                            <div className="dropdown-item">
                                                <div className="dropdown-item-header">
                                                    <span>🌐</span>
                                                    <span>{t('language')}</span>
                                                </div>
                                                <select
                                                    className="dropdown-select"
                                                    value={language}
                                                    onChange={(e) => handleLanguageChange(e.target.value)}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <option value="en">English</option>
                                                    <option value="fa">فارسی</option>
                                                </select>
                                            </div>
                                        </div>

                                        {hasManagementAccess && (
                                            <div className="dropdown-section">
                                                <a
                                                    className="dropdown-item dropdown-item-button"
                                                    href="/api"
                                                >
                                                    <Settings size={18} />
                                                    <span>{t('management_panel')}</span>
                                                </a>
                                            </div>
                                        )}

                                        <div className="dropdown-divider"></div>

                                        <div className="dropdown-section">
                                            <button
                                                className="dropdown-item dropdown-item-button logout-button"
                                                onClick={handleLogout}
                                            >
                                                <LogOut size={18} />
                                                <span>{t('logout_button')}</span>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </nav>
            )}

            {isAuthenticated && floorPlanSrc.length > 0 && isFloorPlanOpen && (
                <div
                    className="modal-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-label={t('floor_plan')}
                    onClick={() => setIsFloorPlanOpen(false)}
                >
                    <div className="modal-card floorplan-modal-card" onClick={(e) => e.stopPropagation()}>
                        <div className="floorplan-modal-header">
                            <div className="floorplan-modal-title">{t('floor_plan')}</div>
                            <button
                                type="button"
                                className="floorplan-close"
                                onClick={() => setIsFloorPlanOpen(false)}
                                aria-label={t('details_close')}
                            >
                                <X size={18} aria-hidden="true" />
                            </button>
                        </div>
                        <div className="floorplan-carousel">
                            {floorPlanSrc.length > 1 && (
                                <button
                                    type="button"
                                    className="floorplan-nav floorplan-prev"
                                    onClick={() => setFloorPlanIndex((i) => Math.max(0, i - 1))}
                                    disabled={floorPlanIndex === 0}
                                    aria-label={t('previous')}
                                >
                                    <ChevronLeft size={18} aria-hidden="true" />
                                </button>
                            )}

                            <img
                                className="floorplan-modal-image"
                                src={floorPlanSrc[floorPlanIndex]}
                                alt={t('floor_plan')}
                                loading="lazy"
                                decoding="async"
                                fetchpriority="low"
                            />

                            {floorPlanSrc.length > 1 && (
                                <button
                                    type="button"
                                    className="floorplan-nav floorplan-next"
                                    onClick={() => setFloorPlanIndex((i) => Math.min(floorPlanSrc.length - 1, i + 1))}
                                    disabled={floorPlanIndex === floorPlanSrc.length - 1}
                                    aria-label={t('next')}
                                >
                                    <ChevronRight size={18} aria-hidden="true" />
                                </button>
                            )}
                        </div>

                        {floorPlanSrc.length > 1 && (
                            <div className="floorplan-dots" aria-hidden="true">
                                {floorPlanSrc.map((_, idx) => (
                                    <button
                                        type="button"
                                        key={idx}
                                        className={`floorplan-dot ${idx === floorPlanIndex ? 'active' : ''}`}
                                        onClick={() => setFloorPlanIndex(idx)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Main content */}
            <main className="layout-main">
                {children}
            </main>

            {/* Footer - hidden on room page */}
            {!isRoomPage && (
                <footer className="footer">
                    © 2025
                </footer>
            )}
        </div>
    );
};

export default Layout;
